/**
 * Note Component
 *
 * Features:
 * - Displays a single note for a lesson, including time, text, and actions.
 * - Allows jumping to the note's timestamp in the video player.
 * - Supports editing and deleting notes.
 * - Passes relevant props to child components for note data and actions.
 * - Uses Material-UI and CSS modules for styling.
 *
 * @param {Object} props - Component properties
 * @param {Object} [props.noteData] - Data object for the note
 * @param {number} [props.idLesson] - Lesson ID associated with the note
 * @param {string} [props.academyName] - Academy name for fetching notes
 * @returns {JSX.Element} Rendered Note component
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import videojs from 'video.js';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BookmarkRemoveIcon from '@mui/icons-material/BookmarkRemove';

import {
  deleteNote,
  editNote,
  getNotes
} from 'store/lesson-actions';

import classes from './Note.module.css';

const Note = props => {
  // videojs instance for controlling the lesson video player
  const myPlayer = videojs('lessonCourse');
  const dispatch = useDispatch();
  // State for note text
  const [noteText, setNoteTex] = useState(props.noteData.entrada);

  /**
   * Handles text change for the note input
   */
  const handleChangeNote = event => {
    setNoteTex(event.target.value);
  };

  /**
   * Jumps to the note's timestamp in the video player
   */
  const changeSecond = () => {
    myPlayer.currentTime(props.noteData.time);
  };

  /**
   * Handles deleting the note and refreshes notes list
   */
  const deleteNoteHandler = () => {
    (async () => {
      const deleteNoteData = await deleteNote(
        props.noteData.idEntradaApunte
      );
      if (deleteNoteData) {
        dispatch(getNotes(props.idLesson, props.academyName));
      }
    })();
  };

  /**
   * Handles saving the note when Enter is pressed
   */
  const saveNoteHandler = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      (async () => {
        await editNote(props.noteData.idEntradaApunte, noteText);
      })();
    }
  };

  return (
    <aside className={classes.noteContainer}>
      {/* Custom component: Button triggers jump to video timestamp */}
      <Button onClick={changeSecond} className={classes.noteBtn}>
        <PlayCircleOutlineIcon /> {props.noteData.segundo}
      </Button>
      {/* Material-UI TextField for note text */}
      <TextField
        value={noteText}
        onChange={handleChangeNote}
        className={classes.noteInput}
        variant="standard"
        InputProps={{
          disableUnderline: true
        }}
        multiline
        onKeyPress={saveNoteHandler}
      />
      {/* Custom component: Button triggers note deletion */}
      <Button
        onClick={deleteNoteHandler}
        className={classes.noteBtn}
      >
        <BookmarkRemoveIcon />
      </Button>
    </aside>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 */
Note.propTypes = {
  /** Data object for the note */
  noteData: PropTypes.object,
  /** Lesson ID associated with the note */
  idLesson: PropTypes.number,
  /** Academy name for fetching notes */
  academyName: PropTypes.string
};

Note.defaultProps = {
  noteData: undefined,
  idLesson: undefined,
  academyName: undefined
};

export default Note;
